import React from 'react';
import styled from 'styled-components';

import { useIncentiveExposure } from 'src/payments/hooks/use-incentive-exposure';
import { useCartCashBackTreatment } from 'src/payments/hooks/use-cart-cash-back-treatment';
import { useIncentives } from 'src/payments/hooks/use-incentives/use-incentives';
import { PBB_INCENTIVES_TERMS_URL } from 'src/payments/constants';
import { Typography } from 'src/typography';
import { WhatIsPayByBank } from 'src/payments/components/what-is-pbb';

export const CartCashBackTreatment = (): JSX.Element | null => {
  const { showTreatment, cashBackValue } = useCartCashBackTreatment();
  const { userType } = useIncentives();

  // Fire only while the treatment is actually visible: hiding it feeds undefined, which resets
  // the canonical hook's dedup ref so a re-shown treatment re-fires.
  useIncentiveExposure({
    incentiveValue: showTreatment ? cashBackValue : undefined,
    userType,
    isCashBack: true,
    source: 'cart',
  });

  if (!showTreatment || cashBackValue === undefined) {
    return null;
  }

  return (
    <Wrapper>
      <Subline data-testid='cart-cash-back-treatment' size='xsmall'>
        Get <strong>${cashBackValue} cash back</strong> on your first pay by bank order.{' '}
        <TermsLink>Terms apply.</TermsLink>
      </Subline>
      <WhatIsPayByBank />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    justify-content: space-between;
    flex-direction: row;
  }
`;

const Subline = styled(Typography.Body)`
  color: var(--dcm-cart-cash-back-text-color, ${({ theme }) => theme.colors.dutchiePayGreen});
  text-align: center;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    text-align: left;
  }
`;

const TermsLink = styled.a.attrs({
  href: PBB_INCENTIVES_TERMS_URL,
  target: '_blank',
  rel: 'noreferrer',
})`
  color: inherit;
  font-weight: 700;
  text-decoration: underline;
`;
